import React, { FC, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import getConfig from 'next/config';
import { Card } from '@deliveroo/tools-component-library';
import { getCurrentUserAndBranch } from 'lib/store/restaurants/selectors';
import { RS_ENABLE_MEDALLIA_SURVEYS } from 'lib/common/features';
import { feedbackUrl } from 'lib/common/constants';
import useI18n from 'lib/hooks/useI18n';
import localeToParts from 'lib/utils/localeToParts';
import styles from './styles.module.scss';

const USER_SURVEYS_TRACKING = 'user-rel-survey-tracking';
const SUBMITTED = 'submitted';
const DISMISSED = 'dismissed';
const {
  publicRuntimeConfig: {
    medalliaEmbedJsScriptUrl,
    medalliaFeedbackSurveyFormId,
    medalliaRelSurveySubmittedQuarantineDays,
    medalliaRelSurveyDismissedQuarantineDays,
  },
} = getConfig();

export type UserInfo = {
  restaurantName: string;
  restaurantDrnId: string;
  organizationDrnId: string;
  userEmail: string;
  userName: string;
  language: string;
  features: string[];
};

export const shouldTriggerSurvey = () => {
  const userTracking = localStorage.getItem(USER_SURVEYS_TRACKING);
  if (!userTracking) {
    return true;
  }
  const userTrackingData = JSON.parse(userTracking);
  const { userAction, dateOfAction } = userTrackingData;
  const currentTime = new Date().valueOf();

  // Quarantine:
  // 3 Days (if dismissed or ignored)
  // 60 Days (if completed)
  if (
    userAction === DISMISSED &&
    currentTime >
      medalliaRelSurveyDismissedQuarantineDays * 24 * 60 * 60 * 1000 +
        dateOfAction
  ) {
    return true;
  } else if (
    userAction === SUBMITTED &&
    currentTime >
      medalliaRelSurveySubmittedQuarantineDays * 24 * 60 * 60 * 1000 +
        dateOfAction
  ) {
    return true;
  }
  return false;
};

const saveUserSurveyTracking = (userAction: string) => {
  const surveyTrackingData = {
    userAction,
    dateOfAction: new Date().valueOf(),
  };
  localStorage.setItem(
    USER_SURVEYS_TRACKING,
    JSON.stringify(surveyTrackingData),
  );
};

const openSurveyContainer = (setShowSurveyContainer: any) => {
  setShowSurveyContainer(true);
};

const closeSurveyContainer = (setShowSurveyContainer: any) => {
  saveUserSurveyTracking(DISMISSED);
  setShowSurveyContainer(false);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.MDIGITAL.EMBEDDED.removeEmbeddedIFrames();
};

const initMedallia = (setShowSurveyContainer: any) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (typeof window.MDIGITAL?.INIT?.init !== 'function') {
    return;
  }
  const triggerSurvey = shouldTriggerSurvey();
  if (!triggerSurvey) {
    return;
  }

  openSurveyContainer(setShowSurveyContainer);

  // To load the survey
  // Based on configured urls and page element in medallia side,
  // survey will be appended to the page element.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.MDIGITAL.INIT.init();
};

export const setMedalliaSettings = (userInfo: UserInfo) => {
  let { language } = localeToParts(userInfo.language || 'en');
  if (userInfo.language.toLowerCase() === 'zh-hk') {
    language = 'zh-hk';
  } else if (language === 'nl') {
    language = 'nl-be';
  } else {
    language = language.toLowerCase();
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.medallia = {
    RESTAURANT_NAME: userInfo.restaurantName,
    RESTAURANT_DRN_ID: userInfo.restaurantDrnId,
    ORGANIZATION_DRN_ID: userInfo.organizationDrnId,
    USER_EMAIL: userInfo.userEmail,
    USERNAME: userInfo.userName,
    LANGUAGE: language,
  };
};

export const loadMedalliaJS = (userInfo: UserInfo) => {
  const medalliaSdk = document.createElement('script');
  medalliaSdk.type = 'text/javascript';
  medalliaSdk.async = true;
  medalliaSdk.src = medalliaEmbedJsScriptUrl;
  document.body.appendChild(medalliaSdk);
  setMedalliaSettings(userInfo);
};

const equalityFn = (left: any, right: any) => {
  return (
    left.user?.id === right.user?.id &&
    left.currentBranch?.id === right.currentBranch?.id
  );
};

export const triggerMedalliaFeedbackSurvey = () => {
  if (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.KAMPYLE_ONSITE_SDK &&
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.KAMPYLE_ONSITE_SDK.loadForm(medalliaFeedbackSurveyFormId)
  ) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.KAMPYLE_ONSITE_SDK.showForm(medalliaFeedbackSurveyFormId);
  } else {
    window.open(feedbackUrl, '_blank');
  }
};

const Medallia: FC = () => {
  const i18n = useI18n();
  const [showSurveyContainer, setShowSurveyContainer] = useState(false);
  const state = useSelector(getCurrentUserAndBranch, equalityFn);

  const { user, currentBranch } = state;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!user || !currentBranch) {
      return null;
    }

    const userInfo: UserInfo = {
      restaurantName: currentBranch.name,
      restaurantDrnId: currentBranch.drnId,
      organizationDrnId: currentBranch.orgDrnId,
      userEmail: user.email,
      userName: user.name,
      features: user.features,
      language: i18n.language,
    };

    window.addEventListener('neb_OnsiteLoaded', function handler(e) {
      e.currentTarget?.removeEventListener(e.type, handler);
      initMedallia(setShowSurveyContainer);
    });

    window.addEventListener('MDigital_Submit_Feedback', (e) => {
      if (e instanceof CustomEvent && e.detail.Form_Type === 'Embedded') {
        closeSurveyContainer(setShowSurveyContainer);
        saveUserSurveyTracking(SUBMITTED);
      }
    });

    window.addEventListener('MDigital_Form_Close_No_Submit', (e) => {
      if (e instanceof CustomEvent && e.detail.Form_Type === 'Embedded') {
        closeSurveyContainer(setShowSurveyContainer);
        saveUserSurveyTracking(DISMISSED);
      }
    });

    if (userInfo.features.includes(RS_ENABLE_MEDALLIA_SURVEYS)) {
      loadMedalliaJS(userInfo);
    }
  });

  return (
    <div>
      {showSurveyContainer ? (
        <Card className={styles.surveyCard}>
          <div
            className={styles.surveyContainer}
            id="restaurant-hub-medallia-survey"
          />
        </Card>
      ) : (
        ''
      )}
    </div>
  );
};

export default Medallia;
